import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'dashboard/ResourceSelector/automationsOnHold';

/**
 * Fetches automations on hold filtered by resource selector type.
 * Uses the dashboard/ResourceSelector endpoint which filters by automation trigger type on the backend.
 *
 * @param typeOf - The resource selector type (e.g., NewDonorAutomationsOnHold, NewLeadAutomationsOnHold)
 * @param enabled - Whether the query should be enabled
 */
export function useAutomationsOnHoldByTypeQuery({
	typeOf,
	enabled = true,
}: {
	typeOf: Api.ResourceSelectorId;
	enabled?: boolean;
}) {
	const userSession = useUserSession();

	return useQuery({
		enabled,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callAsync<Api.IOnHoldAutomationsDashboardCard[]>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `dashboard/ResourceSelector?typeOf=${encodeURIComponent(typeOf)}`,
				}),
				'GET',
				null
			);
			// Extract the values from the first card in the response.
			// The API returns an array with one card per typeOf requested.
			const card = response?.[0];
			return card?.values ?? [];
		},
		queryKey: [BASE_QUERY_KEY, typeOf],
	});
}

export function invalidateAutomationsOnHoldByTypeQuery({ typeOf }: { typeOf: Api.ResourceSelectorId }) {
	return reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY, typeOf] });
}

export function invalidateAllAutomationsOnHoldByTypeQueries() {
	return reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
}
